import { useFrame } from "@react-three/fiber";
import { useEffect, useMemo, useRef, useState } from "react";
import { MeshStandardMaterial, PlaneGeometry, type Group, type Texture } from "three";
import { RoundedBoxGeometry } from "three/examples/jsm/geometries/RoundedBoxGeometry.js";

import { physicalCardFaceTextureCache, type PhysicalCardFaceSpec } from "./CardFaceTextureCache";
import { createPhysicalCardFaceMaterial } from "./physical-card-material";

export const PHYSICAL_CARD_WIDTH = 1.22;
export const PHYSICAL_CARD_HEIGHT = 1.708;
export const PHYSICAL_CARD_DEPTH = 0.055;
export const PHYSICAL_CARD_RADIUS = 0.045;

const CARD_BODY_GEOMETRY = new RoundedBoxGeometry(
  PHYSICAL_CARD_WIDTH,
  PHYSICAL_CARD_HEIGHT,
  PHYSICAL_CARD_DEPTH,
  3,
  PHYSICAL_CARD_RADIUS,
);
const CARD_FACE_GEOMETRY = new PlaneGeometry(
  PHYSICAL_CARD_WIDTH - 0.025,
  PHYSICAL_CARD_HEIGHT - 0.025,
);
const CARD_EDGE_MATERIAL = new MeshStandardMaterial({
  color: "#bca876",
  metalness: 0.08,
  roughness: 0.72,
});

export interface PhysicalCard3DProps {
  readonly face: PhysicalCardFaceSpec;
  readonly position: readonly [number, number, number];
  readonly rotation?: readonly [number, number, number] | undefined;
  readonly scale?: number | undefined;
  readonly selected?: boolean | undefined;
  readonly focused?: boolean | undefined;
  readonly legal?: boolean | undefined;
  readonly dimmed?: boolean | undefined;
  readonly reducedMotion: boolean;
  readonly onActivate?: (() => void) | undefined;
  readonly renderOrder?: number | undefined;
  readonly selectionLift?: number | undefined;
  readonly selectionScale?: number | undefined;
  readonly selectionTilt?: number | undefined;
  readonly sceneMetadata?: PhysicalCardSceneMetadata | undefined;
  readonly onGroupRef?: ((group: Group | null) => void) | undefined;
}

export interface PhysicalCardSceneMetadata {
  readonly objectKind: "physical-card";
  readonly cardInstanceId?: string | undefined;
  readonly cardDefinitionId?: string | undefined;
  readonly cardType?: string | undefined;
  readonly ownerSide: "own" | "opponent" | "hidden" | "neutral";
  readonly laneId?: string | undefined;
  readonly zone: string;
  readonly visibleState: "face-up" | "face-down";
}

export function PhysicalCard3D({
  face,
  position,
  rotation = [-Math.PI / 2, 0, 0],
  scale = 1,
  selected = false,
  focused = false,
  legal = false,
  dimmed = false,
  reducedMotion,
  onActivate,
  renderOrder = 0,
  selectionLift = 0.32,
  selectionScale = 1.055,
  selectionTilt = 0.1,
  sceneMetadata,
  onGroupRef,
}: PhysicalCard3DProps) {
  const groupRef = useRef<Group>(null);
  const [hovered, setHovered] = useState(false);
  const texture = useMemo<Texture>(
    () => physicalCardFaceTextureCache.get(face),
    // The key is the complete cache identity; the object itself is intentionally not a dependency.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [face.key],
  );
  const faceMaterial = useMemo(
    () => createPhysicalCardFaceMaterial({ texture, dimmed, focused, legal, selected }),
    [dimmed, focused, legal, selected, texture],
  );

  useEffect(() => {
    physicalCardFaceTextureCache.retain(face.key);
    return () => {
      physicalCardFaceTextureCache.release(face.key);
      window.setTimeout(() => physicalCardFaceTextureCache.disposeUnretained(), 0);
    };
  }, [face.key]);

  useEffect(() => () => faceMaterial.dispose(), [faceMaterial]);

  useFrame((_, delta) => {
    const group = groupRef.current;
    if (group === null) return;
    const targetLift = selected ? selectionLift : hovered && onActivate !== undefined ? 0.1 : 0;
    const targetTilt = rotation[0] + (selected ? selectionTilt : hovered ? 0.035 : 0);
    const speed = reducedMotion ? 80 : 12;
    const blend = 1 - Math.exp(-speed * delta);
    group.position.x += (position[0] - group.position.x) * blend;
    group.position.y += (position[1] + targetLift - group.position.y) * blend;
    group.position.z += (position[2] - group.position.z) * blend;
    group.rotation.x += (targetTilt - group.rotation.x) * blend;
    group.rotation.y += (rotation[1] - group.rotation.y) * blend;
    group.rotation.z += (rotation[2] - group.rotation.z) * blend;
    const targetScale = scale * (selected ? selectionScale : hovered ? 1.025 : 1);
    group.scale.x += (targetScale - group.scale.x) * blend;
    group.scale.y += (targetScale - group.scale.y) * blend;
    group.scale.z += (targetScale - group.scale.z) * blend;
  });

  return (
    <group
      ref={(group) => {
        groupRef.current = group;
        onGroupRef?.(group);
      }}
      name={
        sceneMetadata === undefined
          ? "physical-card"
          : `physical-card:${sceneMetadata.zone}:${sceneMetadata.ownerSide}`
      }
      userData={{ ...(sceneMetadata ?? {}), focused, selected }}
      position={[position[0], position[1], position[2]]}
      rotation={[rotation[0], rotation[1], rotation[2]]}
      scale={scale}
      onPointerOver={(event) => {
        event.stopPropagation();
        if (onActivate !== undefined) setHovered(true);
      }}
      onPointerOut={() => setHovered(false)}
      onPointerDown={(event) => event.stopPropagation()}
      onClick={(event) => {
        event.stopPropagation();
        onActivate?.();
      }}
    >
      <mesh
        geometry={CARD_BODY_GEOMETRY}
        material={CARD_EDGE_MATERIAL}
        castShadow={!dimmed}
        receiveShadow
        dispose={null}
        renderOrder={renderOrder}
      />
      <mesh
        geometry={CARD_FACE_GEOMETRY}
        material={faceMaterial}
        position={[0, 0, PHYSICAL_CARD_DEPTH / 2 + 0.002]}
        receiveShadow
        dispose={null}
        renderOrder={renderOrder}
      />
    </group>
  );
}
